import { useEffect, useMemo, useState } from 'react';
import { ColorModeProvider, Header } from '@airbus/components-react';
import { useUserContextApi } from '../../context/UserContext';
import styles from './header.module.css';
import { t } from '../../../i18next';
import { useConfigContext } from '../../context/ConfigContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { useAllScreensContext } from '../../context/AllScreensDetailsContext';
import HeaderDesktop, { HeaderSection } from './HeaderDesktop';
import HeaderMobile from './HeaderMobile';
import { BIG_DESKTOP_BREAKPOINT, MOBILE_MEDIA_QUERY } from '../breakpoints';
import { useNavigate } from 'react-router';
import { getApiBaseUrl } from '../../utils/apiBase';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { isPspManagementHidden } from '../../utils/navigationVisibilityUtils';

import type {
  AccessControlItem,
  Props,
  SubChildMenuItem,
  SubMenuItem,
  MenuGroupItem,
   RoleName,
} from './header.types';


function getAppName({ environment, isMobileView }) {
  const appName = isMobileView ? t('app_name_short') : t('app_name');
  return environment ? appName + ` (${environment})` : appName;
}

function Headers({ setShowSideNav }: Readonly<Props>) {
  const { configSchema, environment } = useConfigContext();
  const { loginDetails, userDetails, accessControlDetails, acceptDisclaimer } = useUserContextApi();
  const { totalNotificationCount, setNotificationPanelOpen } = useNotificationContext();
  const { menuGroup } = useAllScreensContext();
  const pspEnabled = useFeatureFlagEnabled('newPspManagement') === true;
  const navigate = useNavigate();
  const apiBase = getApiBaseUrl();

  const [isMobileView, setIsMobileView] = useState<boolean>(() =>
    typeof window === 'undefined' ? false : window.matchMedia(MOBILE_MEDIA_QUERY).matches,
  );

  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window === 'undefined'
      ? true
      : window.matchMedia(`(min-width: ${BIG_DESKTOP_BREAKPOINT}px)`).matches,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mqMobile = window.matchMedia(MOBILE_MEDIA_QUERY);
    const mqDesktop = window.matchMedia(`(min-width: ${BIG_DESKTOP_BREAKPOINT}px)`);

    const onMobileChange = (e: MediaQueryListEvent) => setIsMobileView(e.matches);
    const onDesktopChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);

    mqMobile.addEventListener?.('change', onMobileChange);
    mqDesktop.addEventListener?.('change', onDesktopChange);
    mqMobile.addListener?.(onMobileChange);
    mqDesktop.addListener?.(onDesktopChange);

    setIsMobileView(mqMobile.matches);
    setIsDesktop(mqDesktop.matches);

    return () => {
      mqMobile.removeEventListener?.('change', onMobileChange);
      mqDesktop.removeEventListener?.('change', onDesktopChange);
      mqMobile.removeListener?.(onMobileChange);
      mqDesktop.removeListener?.(onDesktopChange);
    };
  }, []);

  const displayedAppName = getAppName({ environment, isMobileView });

  const userName = userDetails?.mbdUserByUserId
    ? `${userDetails.mbdUserByUserId.firstName} ${userDetails.mbdUserByUserId.lastName}`.trim()
    : loginDetails.userId;

  const [roleNames, setRoleNames] = useState<RoleName[]>([]);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    async function fetchRoleNames() {
      try {
        const data = await fetch(`${apiBase}/api/userRoleNames`).then((r) => r.json());
        const tempRoleNames =
          data?.[`${configSchema}_mbd_users`]?.[0]?.mbdUserToRolesByUserId?.map((r: { mbd_role: RoleName }) => ({
            ...r?.mbd_role,
          })) || [];
        setRoleNames(tempRoleNames);
      } catch {
        setRoleNames([]);
      }
    }
    fetchRoleNames();
  }, [configSchema]);

  const handleLogout = async () => {
    const response = await fetch('/api/auth/session', {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
    });
    if (response.ok) navigate('/login');
  };

  const userMenu = [
    { name: t('profile.profile'), fn: () => setProfileOpen(true) },
    { name: t('logout'), fn: handleLogout },
  ];

  const navigateTo = (appName: string, screenName: number | string) => {
    const route = appName === 'NPI Tool Administration' ? `/admin` : `/mbdcp`;
    navigate(`${route}/${screenName}`);
  };

  const getAccessibleScreens = (accessControlDetails: AccessControlItem[]): Set<string> => {
    return new Set<string>(
      accessControlDetails.map((it: AccessControlItem) => it.mbdScreen.screenInternalName),
    );
  };

  const getFilteredChildren = (sub: SubMenuItem, accessible: Set<string>): SubChildMenuItem[] => {
    if (!Array.isArray(sub.subChildMenu)) {
      return [];
    }

    return sub.subChildMenu.filter(({ appNameId }: SubChildMenuItem) => {
      if (!accessible.has(appNameId)) {
        return false;
      }

      if (isPspManagementHidden(appNameId, pspEnabled)) {
        return false;
      }

      return true;
    });
  };

  const getFilteredSubMenus = (group: MenuGroupItem, accessible: Set<string>): SubMenuItem[] => {
    return (group.subMenu ?? [])
      .map((sub: SubMenuItem) => {
        const children = getFilteredChildren(sub, accessible);

        return {
          ...sub,
          subChildMenu: children,
        };
      })
      .filter((sub: SubMenuItem) => {
        if (isPspManagementHidden(sub.appNameId, pspEnabled)) {
          return false;
        }

        if (accessible.has(sub.appNameId)) {
          return true;
        }

        return (sub.subChildMenu?.length ?? 0) > 0;
      });
  };

  const getSectionItems = (filteredSubMenus: SubMenuItem[]) => {
    return filteredSubMenus
      .flatMap(
        (sub): Array<SubChildMenuItem | SubMenuItem> =>
          sub.subChildMenu?.length ? sub.subChildMenu : [sub],
      )
      .sort(
        (a: SubChildMenuItem | SubMenuItem, b: SubChildMenuItem | SubMenuItem) =>
          (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
      )
      .map((item: SubChildMenuItem | SubMenuItem) => ({
        label: item.screenName,
        appName: item.appName,
        appNameId: item.appNameId,
        sortOrder: item.sortOrder,
      }));
  };

  const createHeaderSection = (
    group: MenuGroupItem,
    accessible: Set<string>,
  ): HeaderSection | null => {
    const filteredSubMenus = getFilteredSubMenus(group, accessible);

    if (!filteredSubMenus.length) {
      return null;
    }

    return {
      label: group.appName,
      items: getSectionItems(filteredSubMenus),
    };
  };

  const sections: HeaderSection[] = useMemo(() => {
    if (!menuGroup || !accessControlDetails?.length || !acceptDisclaimer) {
      return [];
    }

    const accessible = getAccessibleScreens(accessControlDetails);

    return menuGroup
      .map((group: MenuGroupItem) => createHeaderSection(group, accessible))
      .filter(Boolean);
  }, [menuGroup, accessControlDetails, acceptDisclaimer, pspEnabled]);

  return (
    <>
      <ColorModeProvider mode='light'>
        <Header
          appName={displayedAppName}
          className={styles.header}
          onHomeClick={() => navigate('/')}
        >
          {isDesktop ? (
            <HeaderDesktop
              sections={sections}
              notificationCount={totalNotificationCount}
              setNotificationPanelOpen={(open) => setNotificationPanelOpen(open)}
              loginUserId={loginDetails.userId}
              userName={userName}
              userMenu={userMenu}
              profileOpen={profileOpen}
              setProfileOpen={setProfileOpen}
              roleNames={roleNames}
              navigateTo={navigateTo}
            />
          ) : (
            <HeaderMobile
              notificationCount={totalNotificationCount}
              setNotificationPanelOpen={(open) => setNotificationPanelOpen(open)}
              setShowSideNav={setShowSideNav}
              userName={userName}
              handleLogout={handleLogout}
              setProfileOpen={setProfileOpen}
              profileOpen={profileOpen}
              roleNames={roleNames}
            />
          )}
        </Header>
      </ColorModeProvider>
    </>
  );
}

export default Headers;
